import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { A11yModule } from "@angular/cdk/a11y";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { DomPortal } from "@angular/cdk/portal";
import { toSignal } from "@angular/core/rxjs-interop";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import { LanguageConfigStore, LanguageModeService } from "@app/shared";
import {
  CHANNEL_CATALOG,
  EnrichmentChannel,
  buildCustomerViewOptions,
  resolveDefaultPreviewChannelId,
} from "../../../constants/enrichment.constants";
import { EnrichmentStateStore } from "../../../services/state/enrichment-state.store";
import { ChannelId } from "../enrichment-mobile-preview/models/preview.model";
import { EnrichmentMobilePreviewComponent } from "../enrichment-mobile-preview/enrichment-mobile-preview";

/**
 * Thin host that owns the unified **preview toolbar** (surface + view, an
 * independent preview language, side-by-side compare, full-screen expand) and
 * mounts 1–2 chromeless {@link EnrichmentMobilePreviewComponent} instances.
 *
 * Why a host rather than putting the toolbar inside the preview itself: compare
 * needs the toolbar to drive a *sibling* phone. The host is the single owner of
 * preview state; both panes are pure functions of its inputs. The preview's own
 * renderer (the 1k-line `@switch`) is reused verbatim via inputs — never forked.
 *
 * Decoupling: this toolbar's language is fully independent of the editor's hero
 * language switch. Marketing can flip the preview through TH/EN/MM/KH to review
 * without changing which language the forms are editing.
 */
@Component({
  selector: "app-enrichment-preview-panel",
  standalone: true,
  imports: [
    CommonModule,
    A11yModule,
    OmniarkUiModule,
    EnrichmentMobilePreviewComponent,
  ],
  templateUrl: "./enrichment-preview-panel.html",
  styleUrl: "./enrichment-preview-panel.scss",
})
export class EnrichmentPreviewPanel implements OnDestroy {
  private readonly languageConfig = inject(LanguageConfigStore);
  private readonly languageMode = inject(LanguageModeService);
  private readonly overlay = inject(Overlay);
  /** Channel state only — the store is the owner of the editing channel and
   *  carries no HTTP collaborators, unlike the EnrichmentDataService facade. */
  private readonly enrichmentState = inject(EnrichmentStateStore);

  /** The actual panel element. On expand it is *moved* (not re-created) into a
   *  body-level CDK overlay via DomPortal, then returned to the rail on
   *  collapse, so the live preview keeps its state. */
  @ViewChild("panelHost", { static: true })
  private panelHost!: ElementRef<HTMLElement>;

  private overlayRef: OverlayRef | null = null;

  /** Asks the host page to hide the rail. The panel does not own its own
   *  visibility — the edit page does (it also persists the preference) — so
   *  the edge chevron reports the intent rather than acting on it. */
  @Output() readonly collapse = new EventEmitter<void>();

  readonly popId = input<string | null>(null);

  // ── Channel (surface + view) ────────────────────────────────────────────
  /** The channel the editor is working in (the `x-channel` identity). Drives
   *  which customer view the preview opens on and which options are pinned. */
  private readonly editingChannel = toSignal(this.enrichmentState.channel$, {
    initialValue: this.enrichmentState.getChannel(),
  });

  /** The user's explicit toolbar pick. `null` = "follow the editing channel" —
   *  once Marketing picks a surface it is respected for the rest of the
   *  session (the editing channel never overrides a deliberate choice). */
  private readonly pickedChannelId = signal<ChannelId | null>(null);

  /** The desired channel: the user's pick, else the customer view belonging to
   *  the channel being edited (myAIS / AI Chatbot / MC 3.0), else the catalog
   *  default. The child layers device-line correction on top via its own
   *  `effectiveChannelId` — the host never computes a competing value. */
  readonly hostChannelId = computed<ChannelId>(
    () =>
      this.pickedChannelId() ??
      resolveDefaultPreviewChannelId(this.editingChannel()),
  );

  /** Every channel as a single dropdown option, using the full "Surface · View"
   *  label — matches the list-page quick preview so both toolbars read the
   *  same. The editing channel's own views are pinned to the top. */
  readonly channelOptions = computed<SelectOption[]>(() =>
    buildCustomerViewOptions(this.editingChannel()),
  );

  /** Pick a channel from the unified dropdown (locked for device lines). */
  selectChannel(id: ChannelId): void {
    if (this.deviceLocked()) return;
    this.pickedChannelId.set(id);
  }

  readonly activeChannel = computed<EnrichmentChannel>(
    () =>
      CHANNEL_CATALOG.find((c) => c.id === this.hostChannelId()) ??
      CHANNEL_CATALOG[0],
  );

  /** Set when the offering is a device line — the renderer is forced onto the
   *  online-store surface, so the surface picker is locked and a hint shown. */
  readonly deviceLocked = signal(false);

  onDeviceLineChange(isDevice: boolean): void {
    this.deviceLocked.set(isDevice);
    // Keep the surface picker + caption honest: a device offering can only
    // render on the online-store surface (the child forces it). Reflect that
    // in the host's own selection so "Previewing: …" never lies.
    if (isDevice) this.pickedChannelId.set("onlinestore");
  }

  // ── Language (independent of the editor) ──────────────────────────────────
  readonly activeLanguages = computed(() => this.languageConfig.active());

  /** Pane A language (the only language in single mode). `null` = "follow the
   *  channel's primary default", resolved in {@link resolvedLocaleA}. */
  readonly previewLocale = signal<string | null>(null);
  /** Pane B language for side-by-side compare. */
  readonly compareLocaleB = signal<string | null>(null);

  private resolveLocale(chosen: string | null, avoid?: string): string {
    const active = this.activeLanguages();
    if (chosen && active.some((l) => l.locale === chosen)) return chosen;
    const primary =
      this.languageConfig.defaultLocales()[0]?.locale ??
      active[0]?.locale ??
      "th-TH";
    if (avoid && primary === avoid) {
      return active.find((l) => l.locale !== avoid)?.locale ?? primary;
    }
    return primary;
  }

  /** True when the editor's language picker targets a specific language (not
   *  "ALL"). The preview then mirrors that language and its own switcher is
   *  inert; on "ALL" the preview keeps its independent switcher. (#51) */
  readonly languageFollowsForm = computed<boolean>(
    () => this.languageMode.active() !== "ALL",
  );

  readonly resolvedLocaleA = computed<string>(() => {
    // The editor's language picker drives the preview when it targets a single
    // language; on "ALL" the preview uses its own switcher (previewLocale). (#51)
    if (this.languageFollowsForm()) {
      const formLocale = this.languageMode.activeLocale();
      if (this.activeLanguages().some((l) => l.locale === formLocale)) {
        return formLocale;
      }
    }
    return this.resolveLocale(this.previewLocale());
  });
  readonly resolvedLocaleB = computed<string>(() =>
    this.resolveLocale(this.compareLocaleB(), this.resolvedLocaleA()),
  );

  localeLabel(locale: string): string {
    return (
      this.activeLanguages().find((l) => l.locale === locale)?.label ?? locale
    );
  }

  setLanguageA(locale: string): void {
    this.previewLocale.set(locale);
  }
  setLanguageB(locale: string): void {
    this.compareLocaleB.set(locale);
  }
  isLanguageAActive(locale: string): boolean {
    return locale === this.resolvedLocaleA();
  }
  isLanguageBActive(locale: string): boolean {
    return locale === this.resolvedLocaleB();
  }

  // ── Compare ───────────────────────────────────────────────────────────────
  readonly compareMode = signal(false);
  readonly canCompare = computed<boolean>(
    () => this.activeLanguages().length >= 2,
  );

  toggleCompare(): void {
    if (!this.canCompare() && !this.compareMode()) return;
    const next = !this.compareMode();
    this.compareMode.set(next);
    // Two phones never fit in the narrow rail — compare only makes sense in the
    // roomy full-screen surface, so enabling it opens expand automatically.
    if (next) this.openExpand();
  }

  // ── Expand / full-screen ──────────────────────────────────────────────────
  readonly expanded = signal(false);

  toggleExpand(): void {
    if (this.expanded()) this.closeExpand();
    else this.openExpand();
  }

  /**
   * Teleport the panel into a body-level CDK overlay. A plain
   * `position: fixed` overlay does NOT work here: the editor's preview column
   * is `position: sticky`, which creates a stacking context that traps the
   * fixed element's paint order — the app chrome paints over it and no scrim
   * shows. The CDK overlay renders in `.cdk-overlay-container` (body level,
   * z-index 1500), escaping that trap entirely.
   */
  private openExpand(): void {
    this.expanded.set(true);
    if (this.overlayRef) return;
    const ref = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "cdk-overlay-dark-backdrop",
      panelClass: "preview-expand-pane",
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });
    ref.backdropClick().subscribe(() => this.closeExpand());
    // DomPortal MOVES the existing element (with its live preview instances)
    // into the overlay; dispose() returns it to the rail. No re-instantiation,
    // so the previews keep their state across the toggle.
    ref.attach(new DomPortal(this.panelHost));
    this.overlayRef = ref;
  }

  closeExpand(): void {
    this.expanded.set(false);
    // Compare lives inside the full-screen surface; leaving it exits compare.
    this.compareMode.set(false);
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.expanded()) this.closeExpand();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  // ── Caption ───────────────────────────────────────────────────────────────
  readonly caption = computed<string>(() => {
    const surface = this.activeChannel().label;
    if (this.compareMode()) {
      return `Previewing: ${surface} · ${this.localeLabel(
        this.resolvedLocaleA(),
      )} | ${this.localeLabel(this.resolvedLocaleB())}`;
    }
    return `Previewing: ${surface} · ${this.localeLabel(this.resolvedLocaleA())}`;
  });
}
